import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useHeader } from "../../Providers/HeaderContextProvider";
import { getJourneyHome, getCommitments } from "../../Services/journey";
import { routeForAction } from "../Explore/actions";
import {
  JOURNEY_HOME_COPY as C,
  MISSION_DIMENSION_LABEL,
  WHATS_NEXT_DISCOVERY_COPY,
  WHATS_NEXT_CTA,
} from "./copy";
import CheckInFlow from "./CheckIn";
import ModalitySheet from "../Explore/ModalitySheet";
import MissionSheet from "./Mission/MissionSheet";
import { showErrorToast } from "../../Utils/toast";
import "../Explore/style.css";
import { CHECKIN_KIND, CHECKIN_MODALITY, startCheckIn } from "../../Services/checkin";
import { checkInDestination, checkInSheetCopy, completedCheckIn } from "./checkInFlow";
import {
  JourneySectionHead,
  CommitmentRow,
  MomentumDimensions,
  ProgressRows,
} from "./blocks";
import chevronWhite from "../../Assets/icon/journey/chevron-white.svg";
import quickIcon from "../../Assets/icon/journey/next-quick.svg";
import checkInIcon from "../../Assets/icon/journey/checkin.svg";
import "./style.css";

/**
 * Journey Home — v26.5 (guide §5.1, WP-W10; Figma 10905:20092).
 *
 * `GET /ARIS/v4/journey/home-screen` renders every section except Your
 * Commitments, which has no home-screen section: it comes from
 * `GET …/commitments?limit=5`, fetched alongside (WP-W11). Each section
 * degrades to null independently and a null section is hidden — never an
 * error row. Background comes from BackgroundWrapper, never from here.
 *
 * Gone in v26.5 and no longer drawn: Current Focus, Today Aris Noticed, the
 * v1 momentum trend/drivers and the standalone alignment card.
 */

const MissionSection = ({ mission, onOpen, onEvolve }) => {
  if (!mission?.statement) return null;
  const chips = Object.keys(MISSION_DIMENSION_LABEL).filter((key) => mission.dimensions?.[key]);
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.mission.title} subtitle={C.mission.subtitle} />
      <div className="journey-mission-card">
        <p className="journey-mission-card__statement">{mission.statement}</p>
        {chips.length > 0 && (
          <div className="journey-mission-card__chips">
            {chips.map((key) => (
              <span key={key} className="journey-mission-card__chip">
                {MISSION_DIMENSION_LABEL[key]}
              </span>
            ))}
          </div>
        )}
        <button type="button" className="journey-mission-card__link" onClick={onOpen}>
          {C.mission.cta}
          <img src={chevronWhite} alt="" width="16" height="16" />
        </button>
        {/* Mission Evolution (Figma 11268:27950): offered whenever a Mission exists. */}
        <span className="journey-mission-card__rule" aria-hidden="true" />
        <button
          type="button"
          className="journey-mission-card__link journey-mission-card__link--secondary"
          onClick={onEvolve}
        >
          {C.mission.evolve}
          <img src={chevronWhite} alt="" width="16" height="16" />
        </button>
        <span className="journey-mission-card__orb" aria-hidden="true" />
      </div>
    </section>
  );
};

/**
 * 0–3 priorities, in `order`, in the member's own words. A priority is `text`
 * only — there is no description line (§7 J3). An empty list still offers
 * Review Priorities; null (unavailable) hides the section.
 */
const MattersSection = ({ priorities, onReview }) => {
  if (!Array.isArray(priorities)) return null;
  const ordered = priorities
    .filter((p) => p?.text)
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.matters.title} subtitle={C.matters.subtitle} />
      <div className="journey-rows">
        {ordered.map((p) => (
          <div key={p.priority_id || p.text} className="journey-row">
            <span className="journey-row__text">
              <span className="journey-row__title">{p.text}</span>
            </span>
          </div>
        ))}
        <div className="journey-row">
          <button type="button" className="journey-outline-btn" onClick={onReview}>
            {C.matters.cta}
          </button>
        </div>
      </div>
    </section>
  );
};

/**
 * One step or null, switched on `kind` (§5.1d). A Dialogue step routes by
 * `dialogue_id` only; a platform action has none and routes by its action.
 * Progress counts are never drawn as a percentage or ring — this card shows
 * none at all.
 */
const WhatsNextSection = ({ step, navigate }) => {
  if (!step?.kind) return null;
  let title;
  let body;
  let cta;
  let route = null;
  if (step.kind === "DISCOVERY") {
    const copy = WHATS_NEXT_DISCOVERY_COPY[step.discovery_key] || WHATS_NEXT_DISCOVERY_COPY.default;
    ({ title, body } = copy);
    cta = WHATS_NEXT_CTA[step.cta] || WHATS_NEXT_CTA.default;
    route = "/journey/discoveries";
  } else if (step.kind === "AGS_NEXT_STEP") {
    title = step.title;
    body = step.reason;
    cta = WHATS_NEXT_CTA.default;
    route = step.dialogue_id
      ? `/explore/dialogue/${encodeURIComponent(step.dialogue_id)}`
      : routeForAction(step.recommendation_type);
  } else {
    return null;
  }
  if (!title) return null;
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.next.title} subtitle={C.next.subtitle} />
      <div className="journey-card journey-step">
        <span className="journey-step__tile journey-step__tile--blue">
          <img src={quickIcon} alt="" width="24" height="24" />
        </span>
        <div className="journey-step__text">
          <h3 className="journey-step__title">{title}</h3>
          {body && <p className="journey-step__body">{body}</p>}
        </div>
        {route && (
          <button type="button" className="journey-solid-btn" onClick={() => navigate(route)}>
            {cta}
          </button>
        )}
      </div>
    </section>
  );
};

/**
 * Today's Check-In. Completed renders the server's own four strings
 * (`status`, `timestamp`, `message`) and no button — no streak, no counter,
 * no "Check In Again" (§7 C4). Otherwise one button opens the Voice / Text
 * sheet (guide §5.1b); `action.type` RESUME words it as a continue.
 */
const CheckInSection = ({ checkIn, onStart }) => {
  if (!checkIn) return null;
  const done = Boolean(checkIn.checked_in_today) || checkIn.state === "COMPLETED";
  const resume = checkIn.action?.type === "RESUME";
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.checkIn.title} subtitle={C.checkIn.subtitle} />
      <div className="journey-card journey-step">
        <span className="journey-step__tile journey-step__tile--teal">
          <img src={checkInIcon} alt="" width="24" height="24" />
        </span>
        <div className="journey-step__text">
          <h3 className="journey-step__title">
            {done ? checkIn.status || "Completed" : C.checkIn.notYet}
          </h3>
          {done && checkIn.timestamp && <p className="journey-step__body">{checkIn.timestamp}</p>}
        </div>
        {done && checkIn.message && <p className="journey-step__body">{checkIn.message}</p>}
        {!done && (
          <button type="button" className="journey-solid-btn" onClick={onStart}>
            {resume ? C.checkIn.resume : C.checkIn.cta}
          </button>
        )}
      </div>
    </section>
  );
};

const CommitmentsSection = ({ commitments, onOpen, onSeeAll }) => {
  if (!Array.isArray(commitments) || commitments.length === 0) return null;
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.commitments.title} subtitle={C.commitments.subtitle} />
      <div className="journey-rows">
        <div className="journey-row journey-row--head">
          <span className="journey-row__label">{C.commitments.listLabel}</span>
          <button type="button" className="journey-text-btn" onClick={onSeeAll}>
            {C.commitments.seeAll}
          </button>
        </div>
        {commitments.map((c) => (
          <CommitmentRow key={c.id} commitment={c} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
};

const MomentumSection = ({ momentum }) => {
  if (!momentum?.dimensions?.length) return null;
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.momentum.title} subtitle={C.momentum.subtitle} />
      <MomentumDimensions momentum={momentum} />
    </section>
  );
};

const ProgressSection = ({ progress, onOpen, onViewMore }) => {
  const events = Array.isArray(progress?.events) ? progress.events : [];
  if (events.length === 0) return null;
  return (
    <section className="journey-section">
      <JourneySectionHead title={C.progress.title} subtitle={C.progress.subtitle} />
      <div className="journey-rows">
        <ProgressRows events={events.slice(0, 4)} onOpen={onOpen} />
        <div className="journey-row">
          <button type="button" className="journey-outline-btn" onClick={onViewMore}>
            {C.progress.cta}
          </button>
        </div>
      </div>
    </section>
  );
};

function Journey() {
  const navigate = useNavigate();
  const { setHeaderContent, setHeaderContentRightSideContent } = useHeader();
  const [home, setHome] = useState(null);
  const [commitments, setCommitments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // "sheet" → the Voice / Text chooser; "form" → the legacy §5.1a form, used
  // only when /checkin/start 404s for this member.
  const [checkInOpen, setCheckInOpen] = useState(null);
  // "Your Mission" (Figma 11268:28241): a sheet on a phone, a dialog on web.
  const [missionOpen, setMissionOpen] = useState(false);
  const closeMission = useCallback(() => setMissionOpen(false), []);
  const [checkInBusy, setCheckInBusy] = useState(false);

  // A home tab, like Explore: the global header (menu + bell) stays up.
  useEffect(() => {
    setHeaderContent(true);
    setHeaderContentRightSideContent(true);
  }, [setHeaderContent, setHeaderContentRightSideContent]);

  const loadCommitments = useCallback(async () => {
    try {
      const payload = await getCommitments({ limit: 5 });
      setCommitments(Array.isArray(payload?.commitments) ? payload.commitments : null);
    } catch (e) {
      // A failed list hides the card, like any null section.
      console.error("Failed to load commitments", e);
      setCommitments(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getJourneyHome();
        if (!cancelled) setHome(payload || null);
      } catch (_) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    loadCommitments();
    return () => {
      cancelled = true;
    };
  }, [loadCommitments]);

  /**
   * A completed check-in changes more than its own card, so the whole payload
   * is re-read. Until it lands, the card shows completed with no button.
   */
  const onCheckInCompleted = useCallback(async (completion) => {
    setCheckInOpen(null);
    setHome((current) =>
      current
        ? {
            ...current,
            todays_check_in: completedCheckIn(current.todays_check_in, completion),
          }
        : current
    );
    try {
      const payload = await getJourneyHome();
      if (payload) setHome(payload);
    } catch (_) {
      /* The optimistic state above already reflects what the server recorded. */
    }
  }, []);

  /**
   * The check-in is a conversation (guide §5.1b): /start creates or resumes
   * today's session and the member continues in the runtime they picked with
   * `checkin_session_id`. A finished day is a 200 COMPLETED — flip the card,
   * open nothing. A 404 means this member is still on the §5.1a form.
   */
  const onChooseCheckIn = useCallback(
    async (mode) => {
      if (checkInBusy) return;
      setCheckInBusy(true);
      try {
        const res = await startCheckIn(
          mode === "voice" ? CHECKIN_MODALITY.VOICE : CHECKIN_MODALITY.TEXT
        );
        if (res?.kind === CHECKIN_KIND.COMPLETED) {
          await onCheckInCompleted(res.completion);
          return;
        }
        const route = checkInDestination(res?.checkin_session_id, mode);
        if (!route) throw new Error("check-in start returned no session");
        setCheckInOpen(null);
        navigate(route);
      } catch (e) {
        if (e?.response?.status === 404) {
          setCheckInOpen("form");
        } else {
          console.error("Failed to start the check-in", e);
          showErrorToast("We couldn't start your check-in. Please try again.");
        }
      } finally {
        setCheckInBusy(false);
      }
    },
    [checkInBusy, navigate, onCheckInCompleted]
  );

  const checkInSheet = checkInSheetCopy(home?.todays_check_in);

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <header className="journey-home__header">
          <h1 className="journey-page__title">{C.title}</h1>
          <p className="journey-home__subtitle">{C.subtitle}</p>
        </header>

        {loading && (
          <div className="journey-card journey-card--status">Loading your journey…</div>
        )}
        {!loading && error && (
          <div className="journey-card journey-card--status">
            We couldn't load your journey just now. Please try again in a moment.
          </div>
        )}

        {!loading && !error && home && (
          <>
            <MissionSection
              mission={home.mission}
              onOpen={() => setMissionOpen(true)}
              onEvolve={() => navigate("/journey/mission/evolve?entry_source=JOURNEY_HOME")}
            />
            <MattersSection
              priorities={home.what_matters_most}
              onReview={() => navigate("/journey/mission")}
            />
            <WhatsNextSection step={home.whats_next} navigate={navigate} />
            <CheckInSection
              checkIn={home.todays_check_in}
              onStart={() => setCheckInOpen("sheet")}
            />
            <CommitmentsSection
              commitments={commitments}
              onOpen={(c) => navigate(`/journey/commitments/${encodeURIComponent(c.id)}`)}
              onSeeAll={() => navigate("/journey/commitments")}
            />
            <MomentumSection momentum={home.momentum} />
            <ProgressSection
              progress={home.your_progress}
              onOpen={(route) => navigate(route)}
              onViewMore={() => navigate("/journey/progress")}
            />
          </>
        )}
      </div>

      <MissionSheet open={missionOpen} onClose={closeMission} />
      <ModalitySheet
        open={checkInOpen === "sheet"}
        title={checkInSheet.title}
        subtitle={checkInSheet.subtitle}
        labels={checkInSheet.labels}
        busy={checkInBusy}
        onChoose={onChooseCheckIn}
        onClose={() => !checkInBusy && setCheckInOpen(null)}
      />

      {checkInOpen === "form" && (
        <CheckInFlow
          onClose={() => setCheckInOpen(null)}
          onCompleted={() => onCheckInCompleted()}
        />
      )}
    </div>
  );
}

export default Journey;
